/**
 * Locale utility functions for consistent locale handling across the application
 */

/**
 * Maps short locale codes to full locale identifiers
 */
export function getFullLocale(shortLocale: string): string {
  const localeMap: Record<string, string> = {
    'tr': 'tr-TR',
    'dk': 'da-DK', 
    'en': 'en-US'
  }
  return localeMap[shortLocale] || 'en-US'
}

/**
 * Formats a date according to the specified locale
 */
export function formatLocalizedDate(date: Date, shortLocale: string): string {
  return date.toLocaleDateString(getFullLocale(shortLocale))
}

/**
 * Formats a date and time according to the specified locale
 */
export function formatLocalizedDateTime(date: Date, shortLocale: string, options?: Intl.DateTimeFormatOptions): string {
  return date.toLocaleString(getFullLocale(shortLocale), options)
}

/**
 * Formats relative time with i18n support
 */
export function formatTimeAgo(date: Date, t: any): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) {
    return t('notifications.just_now')
  } else if (diffMins < 60) {
    return t('notifications.minutes_ago', { count: diffMins })
  } else if (diffHours < 24) {
    return t('notifications.hours_ago', { count: diffHours })
  } else if (diffDays < 7) {
    return t('notifications.days_ago', { count: diffDays })
  } else {
    // For older dates, use localized date format
    const { locale } = useI18n()
    return formatLocalizedDate(date, locale.value)
  }
}

/**
 * Composable for locale-aware date formatting
 */
export const useLocale = () => {
  const { locale, t } = useI18n()
  
  const fullLocale = computed(() => getFullLocale(locale.value))
  
  const formatDate = (date: Date) => formatLocalizedDate(date, locale.value)
  
  const formatDateTime = (date: Date, options?: Intl.DateTimeFormatOptions) => 
    formatLocalizedDateTime(date, locale.value, options)
  
  const formatRelativeTime = (date: Date) => formatTimeAgo(date, t)
  
  return {
    fullLocale,
    formatDate,
    formatDateTime,
    formatRelativeTime,
    getFullLocale
  }
}